import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { Children, isValidElement } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { carouselClasses } from './classes';
import { CarouselSlide } from './components/carousel-slide';

import type { UseCarouselReturn } from './hooks/use-carousel';

// ----------------------------------------------------------------------

/**
 * ⚠️ **프롭은 `carousel` 하나만 필수다**. 나머지 셋이 없어도 돈다 — `sx` 는 그대로 넘기고,
 * `slotProps` 는 `?.` 로 읽고, `className` 은 `mergeClasses` 가 받는다.
 *
 * ⚠️ 자식은 그대로 그리지 않고 **한 장씩 `CarouselSlide` 로 감싼다** — 엘리먼트가 아닌
 * 자식(문자열 등)은 조용히 버려진다.
 */
export type CarouselProps = {
  sx?: SxProps<Theme>;
  /** `useCarousel()` 결과 */
  carousel: UseCarouselReturn;
  children?: ReactNode;
  slotProps?: { slide?: SxProps<Theme>; container?: SxProps<Theme> };
  className?: string;
};

export function Carousel({ sx, carousel, children, slotProps, className, ...other }: CarouselProps) {
  const { mainRef, options } = carousel;

  const axis = options?.axis ?? 'x';
  const slideSpacing = options?.slideSpacing ?? '0px';

  const renderChildren = () =>
    Children.map(children, (child) => {
      if (isValidElement(child)) {
        const reactChild = child;

        return (
          <CarouselSlide key={reactChild.key} options={carousel.options} sx={slotProps?.slide}>
            {child}
          </CarouselSlide>
        );
      }
      return null;
    });

  return (
    <CarouselRoot
      sx={sx}
      ref={mainRef}
      axis={axis}
      className={mergeClasses([carouselClasses.root, className])}
      {...other}
    >
      <CarouselContainer
        axis={axis}
        slideSpacing={slideSpacing}
        className={carouselClasses.container}
        sx={[
          (theme) => ({
            ...(carousel.pluginNames?.includes('autoHeight') && {
              alignItems: 'flex-start',
              transition: theme.transitions.create(['height'], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.shorter,
              }),
            }),
          }),
          ...(Array.isArray(slotProps?.container)
            ? (slotProps?.container ?? [])
            : [slotProps?.container]),
        ]}
      >
        {renderChildren()}
      </CarouselContainer>
    </CarouselRoot>
  );
}

// ----------------------------------------------------------------------

const CarouselRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['axis', 'sx'].includes(prop),
})<{ axis?: 'x' | 'y' }>(() => ({
  margin: 'auto',
  maxWidth: '100%',
  overflow: 'hidden',
  position: 'relative',
  variants: [{ props: { axis: 'y' }, style: { height: '100%' } }],
}));

const CarouselContainer = styled('ul', {
  shouldForwardProp: (prop: string) => !['axis', 'slideSpacing', 'sx'].includes(prop),
})<{ axis?: 'x' | 'y'; slideSpacing?: string }>(({ slideSpacing }) => ({
  display: 'flex',
  backfaceVisibility: 'hidden',
  variants: [
    {
      props: { axis: 'x' },
      style: {
        touchAction: 'pan-y pinch-zoom',
        marginLeft: `calc(${slideSpacing} * -1)`,
      },
    },
    {
      props: { axis: 'y' },
      style: {
        height: '100%',
        flexDirection: 'column',
        touchAction: 'pan-x pinch-zoom',
        marginTop: `calc(${slideSpacing} * -1)`,
      },
    },
  ],
}));
